import React, { useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { Phone } from 'phoneNumberUtils';
import { FieldRenderProps } from 'react-final-form';
import { countriesCall, CountryCodes, findCallByCountry } from 'data/countries';
import Select, {
  OptionValue,
} from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import { useTranslation } from 'react-i18next';
import { Button, Input } from 'ncoded-component-library';
import VerifiedBadge from 'components/VerifiedBadge';
import NotVerifiedBadge from 'components/NotVerifiedBadge';
import HintMessage from 'components/HintMessage';
import { isEnterPressed } from 'utils';

import 'flag-icons/css/flag-icons.min.css';
import './PhoneField.styles.scss';

type PhoneFieldProps = {
  className?: string;
  verified?: boolean;
  /**
   * The number sits on this account but its verification was transferred to
   * whoever proved they hold the SIM, so it can never be verified here again.
   */
  verifiedElsewhere?: boolean;
  /** The number is verified on another account and can be claimed by SMS. */
  conflict?: boolean;
  /** Three failed attempts on this number within 24h, counted across accounts. */
  limitReached?: boolean;
  showInitialError?: boolean;
  onVerification?: () => void;
  onConfirmNumber?: () => void;
  onAddAnotherNumber?: () => void;
} & FieldRenderProps<Phone, HTMLElement>;

const PhoneField: React.FC<PhoneFieldProps> = (props) => {
  const {
    className,
    disabled,
    required,
    input: { value, onChange, ...restInput },
    meta: { error, touched },
    verified = false,
    verifiedElsewhere = false,
    conflict = false,
    limitReached = false,
    showInitialError = false,
    onVerification,
    onConfirmNumber,
    onAddAnotherNumber,
    ...rest
  } = props;

  const classes = classNames('phone-field', className);

  const phoneInputRef = useRef<HTMLInputElement>(null);

  // The field is about to be empty, so put the caret in it.
  const addAnotherNumber = useCallback(() => {
    onAddAnotherNumber?.();
    phoneInputRef.current?.focus();
  }, [onAddAnotherNumber]);

  const hasError = useMemo(
    () => error && (touched || showInitialError),
    [error, showInitialError, touched],
  );

  const { t } = useTranslation();

  const suffixNode = useMemo(() => {
    // Verifying can never succeed while the number belongs to another account,
    // so the button is withheld rather than offered and rejected.
    if (verified || verifiedElsewhere || !onVerification) return null;

    return (
      <Button
        className="verify"
        variant="outline"
        disabled={!!error || !value?.phoneNumber || disabled}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onVerification?.();
        }}
      >
        {t('verify')}
      </Button>
    );
  }, [
    verified,
    verifiedElsewhere,
    onVerification,
    error,
    value?.phoneNumber,
    disabled,
    t,
  ]);

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const phoneNumber = event.target.value;

      onChange({
        ...value,
        phoneNumber,
      });
    },
    [onChange, value],
  );

  const handleOnCountryCall = useCallback(
    (option: OptionValue<CountryCodes>) => {
      onChange({
        ...value,
        country: option.value,
        countryCode: findCallByCountry(option.value),
      });
    },
    [value, onChange],
  );

  return (
    <div
      className={classes}
      onKeyDown={(e) => {
        if (isEnterPressed(e)) {
          e.preventDefault();
        }
      }}
    >
      <div className="phone-field__container">
        <div className="phone-field__container__country">
          <label className={classNames({ required })}>{t('country')}</label>

          <Select
            className="country-code"
            searchable
            hasError={hasError}
            options={countriesCall}
            value={value.country}
            disabled={disabled}
            onChange={handleOnCountryCall}
            renderAsPortal
            portalClassName="cn-sel-port"
          />
        </div>

        <div className="phone-field__container__phone">
          <label className={classNames({ required })}>{t('phoneNumber')}</label>

          <div
            className="phone-field__container__phone__input-container"
            onKeyDown={(e) => {
              if (!verified && isEnterPressed(e)) onVerification?.();
            }}
          >
            <Input
              {...rest}
              {...restInput}
              ref={phoneInputRef}
              type="tel"
              hasError={hasError}
              disabled={disabled}
              className="phone-field__container__phone__input-container__phone-number"
              placeholder={t('phoneNumber')}
              value={value.phoneNumber}
              onChange={handleOnChange}
              suffixNode={suffixNode}
            />
            {verified && <VerifiedBadge text={t('verified')} />}
            {verifiedElsewhere && <NotVerifiedBadge text={t('notVerified')} />}
          </div>
        </div>
      </div>

      {verifiedElsewhere && (
        <HintMessage
          className="phone-field__verified-elsewhere"
          type="info"
          message={t('numberVerifiedOnAnotherAccount')}
          {...(onAddAnotherNumber &&
            !disabled && {
              actionButtonText: t('addAnotherNumber'),
              actionButtonVariant: 'outline' as const,
              onActionClick: addAnotherNumber,
            })}
        />
      )}

      {conflict && (
        <HintMessage
          className="phone-field__conflict"
          type="info"
          message={t('numberAlreadyVerifiedOnAnotherAccount')}
          {...(onConfirmNumber &&
            !disabled && {
              actionButtonText: t('confirmThisIsMyNumber'),
              actionButtonVariant: 'outline' as const,
              onActionClick: onConfirmNumber,
            })}
        />
      )}

      {limitReached && (
        <HintMessage
          className="phone-field__limit-reached"
          type="error"
          message={t('tooManyAttemptsOnThisNumber')}
          {...(onAddAnotherNumber &&
            !disabled && {
              actionButtonText: t('useAnotherNumber'),
              actionButtonVariant: 'outline' as const,
              onActionClick: addAnotherNumber,
            })}
        />
      )}

      {hasError && <p className="phone-field__error">{error}</p>}
    </div>
  );
};

export default PhoneField;
